import { Component, Input, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { SkeletonModule } from "primeng/skeleton";

/**
 * Available preset names for the list page skeleton
 */
export type ListPageSkeletonPreset =
  | "paymentIntegrations"
  | "customers"
  | "scheduledOrders"
  | "products"
  | "coupons"
  | "quickLinks"
  | "productGroups"
  | "retentionWorkflows";

/**
 * Standardized List Page Skeleton Component
 *
 * A configurable skeleton component for list pages with sidebar filters and data tables.
 * This is the single source of truth for all list page loading states.
 * Uses the same layout and structure as the customers skeleton component.
 *
 * @example
 * ```html
 * <app-list-page-skeleton
 *   [sidebarCollapsed]="sidebarCollapsed()"
 *   filterPreset="paymentIntegrations"
 *   tablePreset="paymentIntegrations"
 * />
 * ```
 */
@Component({
  selector: "app-list-page-skeleton",
  standalone: true,
  imports: [CommonModule, SkeletonModule],
  templateUrl: "./list-page-skeleton.component.html",
  styleUrl: "./list-page-skeleton.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListPageSkeletonComponent {
  /** Whether the filter sidebar is collapsed */
  @Input() sidebarCollapsed = false;

  /** Preset name for filters */
  @Input() filterPreset: ListPageSkeletonPreset = "customers";

  /** Preset name for table columns */
  @Input() tablePreset: ListPageSkeletonPreset = "customers";

  /** Number of skeleton rows to display */
  @Input() rows = 15;

  /** Whether to show the toolbar */
  @Input() showToolbar = true;

  /** Whether to show pagination */
  @Input() showPagination = true;

  /** Whether to show actions column */
  @Input() showActions = true;

  /** Get filter preset name for template switch */
  get filterPresetName(): string {
    return this.filterPreset;
  }

  /** Get table preset name for template switch */
  get tablePresetName(): string {
    return this.tablePreset;
  }

  /** Generate array for row iteration */
  get rowsArray(): number[] {
    return Array.from({ length: this.rows }, (_, i) => i + 1);
  }
}
